import React from 'react';
import {
  ActionIcon,
  Badge, Card, DetailStack, FontWeight, HStack, ICON_EYE_OUTLINE, Stack, Text, UserPreview,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { SerpInvoice } from '../../../providers/api/dtos';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import ViewContactPopoverPreview from '../../../contact/containers/view-contact-popover-preview/ViewContactPopoverPreview';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { invoiceTypeLabels } from '../../../labels/invoiceTypeLabels';
import { InvoiceStatus, ReferenceType } from '../../../domain/types';
import DateViewer from '../../../components/date-viewer/DateViewer';
import Divider from '../../../components/divider/Divider';
import { getCriticalIfInThePast, getPaymentDateLabel } from '../utils';
import { goToDetailPage } from '../../../utils/navigate/utils';
import UpdateInvoiceStatus, { UpdateInvoiceStatusProps } from '../update-invoice-status/UpdateInvoiceStatus';
import ViewCompanyPopoverPreview from '../../../company/containers/view-company-popover-preview/ViewCompanyPopoverPreview';
import CompanyAvatar from '../../../company/components/company-avatar/CompanyAvatar';
import InvoiceValuePopover from '../../components/invoice-value-popover/InvoiceValuePopover';
import EntityReferenceBadge from '../../../components/entity-reference-badge/EntityReferenceBadge';
import ViewAgentPopoverPreview from '../../../agent/containers/view-agent-popover-preview/ViewAgentPopoverPreview';
import { ViewAgentSmallPreview } from '../../../agent/components';
import { toAgentPreviewProps } from '../../../agent/mappers/toAgentPreviewProps';
import { computeGrossValue } from '../../utils';
import TextPreview from '../../../components/text-preview/TextPreview';

export interface ViewSerpInvoiceTableCardProps {
  serpInvoice: SerpInvoice,
  onInvoiceUpdated?: UpdateInvoiceStatusProps['onSuccess'],
  canEdit?: boolean,
}

const ViewSerpInvoiceTableCard: React.FC<ViewSerpInvoiceTableCardProps> = ({
  serpInvoice,
  onInvoiceUpdated,
  canEdit = false,
}) => {
  const {
    invoice, property, contact, company, agent,
  } = serpInvoice;

  const goToInvoiceDetailPage = React.useCallback(() => {
    goToDetailPage(invoice.id!, ReferenceType.INVOICE);
  }, [invoice]);

  const paymentDateLabel = React.useMemo(() => getPaymentDateLabel(invoice.status), [invoice.status]);
  const grossValue = React.useMemo(() => computeGrossValue(invoice.value, invoice.vat), [invoice.value, invoice.vat]);
  const amountLabel = grossValue ? 'Importo' : 'Importo (netto)';
  const displayAmount = grossValue ?? invoice.value;

  const noteToDisplay = React.useMemo(
    () => invoice.notes?.[0],
    [invoice.notes],
  );

  return (
    <Card>
      <Card.Header
        primaryActions={[(
          <ActionIcon
            label="Visualizza"
            aria-label="Visualizza dettagli pagamento"
            icon={{ path: ICON_EYE_OUTLINE }}
            size="S"
            onClick={goToInvoiceDetailPage}
          />
        )]}
      >
        {contact && (
          <ViewContactPopoverPreview
            contact={contact}
            trigger={(
              <UserPreview
                name={contact.name || NO_VALUE_SYMBOL}
                size="S"
              >
                {contact.phoneNumber || NO_VALUE_SYMBOL}
              </UserPreview>
            )}
          />
        )}
        {company && (
          <ViewCompanyPopoverPreview
            company={company}
            trigger={(
              <HStack gap={100} wrap="nowrap">
                <CompanyAvatar size="S" />
                <Text.BodySmall
                  fontWeight={FontWeight.MEDIUM}
                  css={setVerticalEllipsis(2)}
                >
                  {company.name}
                </Text.BodySmall>
              </HStack>
            )}
          />
        )}
      </Card.Header>
      <Card.Content>
        <Stack gap={150}>
          <HStack gap={150} wrap="nowrap">
            <EntityReferenceBadge
              id={property.id!}
              entity={ReferenceType.PROPERTY}
              label={property.referenceId!}
              isNewTab
            />
            <Badge
              label={invoiceTypeLabels[invoice.type]}
              color="neutral"
              size="XS"
            />
            <UpdateInvoiceStatus
              invoice={invoice}
              emphasis="low"
              size="XS"
              onSuccess={onInvoiceUpdated}
              canEditType={canEdit}
            />
          </HStack>
          <HStack gap={150} wrap="nowrap">
            <ViewAgentPopoverPreview
              agent={agent}
              trigger={(
                <ViewAgentSmallPreview
                  {...toAgentPreviewProps(agent, { omit: { name: true } })}
                  size="S"
                  expanded
                />
              )}
            />
            <DetailStack label={amountLabel}>
              <InvoiceValuePopover
                invoice={invoice}
                trigger={(
                  <Text.Caption
                    fontWeight={FontWeight.REGULAR}
                    whitespace="nowrap"
                    lineHeight="1"
                  >
                    {formatEuro(displayAmount)}
                  </Text.Caption>
                )}
              />
            </DetailStack>
            <DetailStack label={paymentDateLabel}>
              <DateViewer stringDate={invoice.paymentDate} highlightWhen={invoice.status !== InvoiceStatus.COLLECTED ? getCriticalIfInThePast : undefined} />
            </DetailStack>
          </HStack>
        </Stack>
        {(invoice.status !== InvoiceStatus.DRAFT || noteToDisplay) && (
          <>
            <Divider />
            <HStack gap={150} wrap="nowrap">
              {[InvoiceStatus.PROFORMA, InvoiceStatus.NOT_PAID].includes(invoice.status) && (
                <DetailStack label="Rif. Proforma">
                  {invoice.proformaInvoiceId}
                </DetailStack>
              )}
              {[InvoiceStatus.PAID, InvoiceStatus.COLLECTED].includes(invoice.status) && (
                <DetailStack label="Rif. Fattura">
                  {invoice.invoiceId}
                </DetailStack>
              )}
              {noteToDisplay && (
                <TextPreview
                  aria-label="Note sul pagamento"
                  text={noteToDisplay.text}
                  author={noteToDisplay.author}
                  date={noteToDisplay.date}
                />
              )}
            </HStack>
          </>
        )}
      </Card.Content>
    </Card>
  );
};

export default ViewSerpInvoiceTableCard;
